import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion, useReducedMotion } from 'framer-motion';

// ── Intent ───────────────────────────────────────────────────────────────────
// Fan de Umamusume llegando al calculador: anticipación antes de la carrera.
// Paleta: #FAB0C0 rosa pastel + #48E5B9 turquesa (del mundo del logo SVG).
// Signature: Un punto luminoso que orbita el logo como un caballo en la pista.
// Principio del skill: "one well-orchestrated page load" — 3 actos secuenciales,
// no 6 elementos compitiendo simultáneamente por atención.
// ─────────────────────────────────────────────────────────────────────────────

// animation-designer § Pattern 2: Staggered reveal — contenedor orquestra
// el orden de aparición de los hijos. staggerChildren hace el trabajo.
const containerVariants = {
    hidden: { opacity: 0 },
    show: {
        opacity: 1,
        transition: { staggerChildren: 0.22, delayChildren: 0.1 }
    }
};

// Cada acto: fade + slide suave hacia arriba
const fadeUp = {
    hidden: { opacity: 0, y: 14 },
    show: { opacity: 1, y: 0, transition: { duration: 0.65, ease: 'easeOut' } }
};

const LoadingScreen = () => {
    const { t } = useTranslation();

    // animation-designer § Reduce Motion: usuarios sensibles al movimiento
    // reciben solo fades, sin traslaciones ni rotaciones.
    const shouldReduceMotion = useReducedMotion();

    const resolvedFadeUp = shouldReduceMotion
        ? { hidden: { opacity: 0 }, show: { opacity: 1, transition: { duration: 0.3 } } }
        : fadeUp;

    return (
        <div className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-[#060810] overflow-hidden">

            {/* ── Atmósfera de fondo ────────────────────────────────────────────────
          frontend-design § Backgrounds: UN solo orb centrado muy sutil.
          Nada en las esquinas, nada compitiendo. El foco va al centro.
          interface-design § Color Lives Somewhere: el color viene del logo. */}
            <div
                className="absolute inset-0 flex items-center justify-center pointer-events-none"
                aria-hidden="true"
            >
                <div className="w-[520px] h-[520px] rounded-full bg-[#FAB0C0]/7 blur-[140px]" />
            </div>

            {/* ── Contenido principal — orquestado en 3 actos ──────────────────── */}
            <motion.div
                className="relative z-10 flex flex-col items-center gap-10"
                variants={containerVariants}
                initial="hidden"
                animate="show"
            >

                {/* ── ACTO 1: Logo + Arco orbital ────────────────────────────────── */}
                <motion.div
                    className="relative flex items-center justify-center"
                    style={{ width: 160, height: 160 }}
                    variants={resolvedFadeUp}
                >
                    {/* Arco SVG que se dibuja solo (pathLength 0→0.78)
              animation-designer § SuccessCheckmark pattern: pathLength en SVG.
              La animación "dibuja" el arco en lugar de simplemente aparecer.
              Empieza en las 12h (rotate -90°). 0.78 = ~280° del círculo. */}
                    <svg
                        className="absolute inset-0 w-full h-full"
                        viewBox="0 0 160 160"
                        fill="none"
                        aria-hidden="true"
                    >
                        {/* Círculo base fantasma — da contexto sin robar atención */}
                        <circle cx="80" cy="80" r="72" stroke="rgba(255,255,255,0.04)" strokeWidth="1" />

                        {/* Arco principal rosa — se dibuja con pathLength */}
                        <motion.circle
                            cx="80" cy="80" r="72"
                            stroke="#FAB0C0"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            style={{ transformOrigin: '80px 80px', rotate: '-90deg' }}
                            initial={{ pathLength: 0, opacity: 0 }}
                            animate={{ pathLength: 0.78, opacity: 0.65 }}
                            transition={{ duration: 1.5, delay: 0.25, ease: [0.4, 0, 0.2, 1] }}
                        />

                        {/* Acento turquesa corto al lado opuesto — aparece después del arco rosa.
                interface-design § Border Progression: intensidades distintas. */}
                        <motion.circle
                            cx="80" cy="80" r="72"
                            stroke="#48E5B9"
                            strokeWidth="1"
                            strokeLinecap="round"
                            style={{ transformOrigin: '80px 80px', rotate: '85deg' }}
                            initial={{ pathLength: 0, opacity: 0 }}
                            animate={{ pathLength: 0.14, opacity: 0.45 }}
                            transition={{ duration: 0.7, delay: 1.4, ease: 'easeOut' }}
                        />
                    </svg>

                    {/* Punto orbital luminoso — la firma visual de este loading.
              "Un caballo dando vueltas en la pista." Aparece después del arco.
              animation-designer: GPU-accelerated rotate, sin layout triggers.
              Solo se muestra si el usuario no prefiere movimiento reducido. */}
                    {!shouldReduceMotion && (
                        <motion.div
                            className="absolute"
                            style={{ width: 160, height: 160, top: 0, left: 0 }}
                            initial={{ rotate: -90, opacity: 0 }}
                            animate={{ rotate: [null, 270] }}
                            transition={{
                                rotate: { duration: 3.5, repeat: Infinity, ease: 'linear', repeatDelay: 0 },
                                opacity: { duration: 0.4, delay: 1.7 }
                            }}
                        >
                            {/* Punto en el borde superior del contenedor = radio 72 desde centro */}
                            <div
                                className="absolute left-1/2 -translate-x-1/2"
                                style={{ top: 8 }}
                            >
                                <motion.div
                                    className="w-2 h-2 rounded-full bg-[#FAB0C0]"
                                    style={{ boxShadow: '0 0 10px 3px rgba(250,176,192,0.7)' }}
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    transition={{ delay: 1.7, duration: 0.4 }}
                                />
                            </div>
                        </motion.div>
                    )}

                    {/* Logo centrado — entra con spring suave (scale 0.85→1 con overshoot leve).
              El easing [0.34, 1.2, 0.64, 1] es un cubic-bezier con micro-bounce.
              animation-designer: solo scale + opacity, GPU-accelerated. */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.82 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.9, delay: 0.1, ease: [0.34, 1.2, 0.64, 1] }}
                    >
                        <img
                            src="/images/Logo/Umacalculator_logo.svg"
                            alt="Umacalculator"
                            className="w-20 h-20 object-contain"
                            draggable={false}
                        />
                    </motion.div>
                </motion.div>

                {/* ── ACTO 2: Tipografía ─────────────────────────────────────────── */}
                {/* interface-design § Typography: dos niveles claros. Título bold +
            tracking muy abierto. Subtítulo mono muted. Sin decoraciones extra. */}
                <motion.div
                    className="flex flex-col items-center gap-2"
                    variants={resolvedFadeUp}
                >
                    <h2 className="text-white/85 font-bold tracking-[0.45em] uppercase text-sm select-none">
                        {t('LoadingScreen.title', 'Loading')}
                    </h2>
                    <p className="text-white/30 font-mono tracking-widest text-[10px] uppercase select-none">
                        {t('LoadingScreen.subtitle', 'Retrieving Data')}
                    </p>
                </motion.div>

                {/* ── ACTO 3: Indicador de progreso ─────────────────────────────── */}
                {/* Un solo shimmer que recorre una línea. Reemplaza el ping dot + texto cyan.
            animation-designer § Performance: solo translateX (GPU-accelerated).
            Menos ruido visual, más claridad de intención. */}
                <motion.div variants={resolvedFadeUp}>
                    <div className="w-28 h-[1.5px] bg-white/8 rounded-full overflow-hidden relative">
                        {!shouldReduceMotion ? (
                            <motion.div
                                className="absolute inset-y-0 left-0 w-1/2"
                                style={{
                                    background: 'linear-gradient(90deg, transparent, #FAB0C0, transparent)'
                                }}
                                animate={{ x: ['-100%', '200%'] }}
                                transition={{
                                    duration: 1.6,
                                    repeat: Infinity,
                                    ease: 'linear',
                                    delay: 1.5
                                }}
                            />
                        ) : (
                            // Reduce motion: línea estática con gradiente en lugar de shimmer animado
                            <div
                                className="absolute inset-0"
                                style={{ background: 'linear-gradient(90deg, transparent, rgba(250,176,192,0.4), transparent)' }}
                            />
                        )}
                    </div>
                </motion.div>

            </motion.div>
        </div>
    );
};

export default LoadingScreen;
// Revisado: 2026-02-21 [UI/UX Overhaul - Guidelines Applied]
